import React, { useState, useEffect } from "react";
import {
	Modal,
	TextInput,
	Textarea,
	Table,
	Title,
	Checkbox,
	Button,
	Flex,
	Pagination,
	Stack,
	Grid,
} from "@mantine/core";
import zIndex from "../../designTokens/zIndex";
import { usePagination } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { IconSearch } from "@tabler/icons-react";
import { IconPlus } from "@tabler/icons-react";
import { IconPencilCheck } from "@tabler/icons-react";
import { IconX } from "@tabler/icons-react";
import { useUser } from "../../contexts/UserContext";
import { CardPost } from "../Card/CardPost";

const UpdateCollectionModal = ({
	modalOpen,
	setModalOpen,
	title,
	setTitle,
	description,
	setDescription,
	threadsList,
	selectedThreads,
	handleThreadSelect,
	isMobile,
	collectionList,
	onUpdateCollection,
	handleRemoveThread,
	isNew = true,
}) => {
	const { t } = useTranslation();
	const user = useUser();
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredThreads, setFilteredThreads] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const threadsPerPage = 10; // Adjust the number of threads per page as needed

	const [sortConfig, setSortConfig] = useState({
		key: null,
		direction: "asc",
	});

	const sortThreads = (threads) => {
		if (!sortConfig.key) return threads;

		return [...threads].sort((a, b) => {
			if (sortConfig.key === "date") {
				const dateA = new Date(a.timestamp);
				const dateB = new Date(b.timestamp);
				return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
			}
			if (
				sortConfig.key === "engagementRate" ||
				sortConfig.key === "engagementAmount"
			) {
				const valA =
					sortConfig.key === "engagementRate"
						? ((a.likes + a.replies + a.reposts) / a.views) * 100
						: a.likes + a.replies + a.reposts;
				const valB =
					sortConfig.key === "engagementRate"
						? ((b.likes + b.replies + b.reposts) / b.views) * 100
						: b.likes + b.replies + b.reposts;
				return sortConfig.direction === "asc" ? valA - valB : valB - valA;
			}
			if (sortConfig.direction === "asc") {
				return a[sortConfig.key] - b[sortConfig.key];
			}
			return b[sortConfig.key] - a[sortConfig.key];
		});
	};

	const handleSort = (key) => {
		setSortConfig((current) => ({
			key,
			direction:
				current.key === key && current.direction === "asc" ? "desc" : "asc",
		}));
	};

	useEffect(() => {
		const filtered = threadsList.filter(
			(thread) =>
				thread.text &&
				thread.text.toLowerCase().includes(searchQuery.toLowerCase())
		);
		const sorted = sortThreads(filtered);
		setFilteredThreads(sorted);
		setCurrentPage(1);
	}, [searchQuery, threadsList, sortConfig]);

	const indexOfLastThread = currentPage * threadsPerPage;
	const indexOfFirstThread = indexOfLastThread - threadsPerPage;
	const currentThreads = filteredThreads.slice(
		indexOfFirstThread,
		indexOfLastThread
	);

	const columns = [
		{ key: "views", label: "Views", mobileHidden: true },
		{ key: "likes", label: "Likes", mobileHidden: true },
		{ key: "replies", label: "Replies", mobileHidden: true },
		{ key: "reposts", label: "Reposts", mobileHidden: true },
	];

	return (
		<Modal
			size={isMobile ? "95%" : "80%"}
			h={isMobile ? "95%" : "80%"}
			opened={modalOpen}
			onClose={() => setModalOpen(false)}
			title={t("myCollection.create")}
			zIndex={zIndex.modal}
		>
			<Stack gap={48}>
				<Stack gap={24}>
					<TextInput
						size={"xl"}
						label={t("myCollection.collectionTitle")}
						placeholder={t("myCollection.collectionTitlePlaceholder")}
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						maxLength={50}
					/>
					<Textarea
						size={"xl"}
						label={t("myCollection.collectionDescription")}
						placeholder={t("myCollection.collectionDescriptionPlaceholder")}
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						maxLength={200}
					/>
					<TextInput
						label={t("myCollection.searchThreads")}
						placeholder={t("myCollection.searchThreads")}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						leftSection={<IconSearch size={24} />}
						size={"xl"}
					/>
				</Stack>
				<Table highlightOnHover fz={isMobile ? "md" : "lg"}>
					<Table.Thead style={{ display: "block", width: "100%" }}>
						<Table.Tr style={{ display: "flex", width: "100%" }}>
							<Table.Th p={isMobile ? 8 : 16} style={{ width: "32px" }}>
								{/* Adjusted flex for checkbox */}
							</Table.Th>
							<Table.Th
								p={isMobile ? 8 : 16}
								style={{ width: "80px", cursor: "pointer" }}
								onClick={() => handleSort("date")}
							>
								<Flex direction="row" align="center">
									Date
									{sortConfig.key === "date" && (
										<span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
									)}
								</Flex>
							</Table.Th>
							<Table.Th p={isMobile ? 8 : 16} style={{ flex: "1" }}>
								<Flex direction="row" align="center">
									Content
								</Flex>
							</Table.Th>
							{columns.map((col) =>
								!isMobile || !col.mobileHidden ? (
									<Table.Th
										key={col.key}
										p={isMobile ? 8 : 16}
										style={{ width: "96px", cursor: "pointer" }}
										onClick={() => handleSort(col.key)}
									>
										<Flex direction="row" align="center">
											{col.label}
											{sortConfig.key === col.key && (
												<span>
													{sortConfig.direction === "asc" ? " ↑" : " ↓"}
												</span>
											)}
										</Flex>
									</Table.Th>
								) : null
							)}
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody
						style={{
							display: "block",
							overflow: "auto",
							minHeight: "200px",
							maxHeight: isMobile ? "40vh" : "50vh",
							height: isMobile ? "40vh" : "50vh",
							width: "100%",
						}}
					>
						{currentThreads.map((thread) => (
							<Table.Tr
								key={thread.id}
								style={{ display: "flex", width: "100%" }}
							>
								<Table.Td p={isMobile ? 8 : 16} style={{ width: "32px" }}>
									{/* Adjusted flex for checkbox */}
									<Checkbox
										checked={selectedThreads.includes(thread.id)}
										onChange={() => handleThreadSelect(thread.id)}
									/>
								</Table.Td>
								{!isMobile && (
									<Table.Td
										p={isMobile ? 8 : 16}
										style={{ width: "80px", fontSize: "14px" }}
									>
										{new Date(thread.timestamp)
											.toISOString()
											.slice(2, 10)
											.replace(/-/g, "/")}
									</Table.Td>
								)}
								<Table.Td p={isMobile ? 8 : 16} style={{ flex: "1" }}>
									<a
										href={thread.permalink}
										target="_blank"
										rel="noreferrer"
										style={{
											display: "-webkit-box",
											WebkitLineClamp: 1,
											WebkitBoxOrient: "vertical",
											overflow: "hidden",
											fontSize: "16px",
											fontWeight: "bold",
											textOverflow: "ellipsis",
										}}
									>
										{thread.text.split(".")[0].replace(/�/g, "")}
									</a>
								</Table.Td>
								{!isMobile && (
									<>
										<Table.Td p={isMobile ? 8 : 16} style={{ width: "96px" }}>
											{thread.views}
										</Table.Td>
										<Table.Td p={isMobile ? 8 : 16} style={{ width: "96px" }}>
											{thread.likes}
										</Table.Td>
										<Table.Td p={isMobile ? 8 : 16} style={{ width: "96px" }}>
											{thread.replies}
										</Table.Td>
										<Table.Td p={isMobile ? 8 : 16} style={{ width: "96px" }}>
											{thread.reposts}
										</Table.Td>
									</>
								)}
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
				<Flex justify="center" w="100%" style={{ overflow: "hidden" }}>
					<Pagination
						size={isMobile ? "sm" : "lg"}
						total={Math.ceil(filteredThreads.length / threadsPerPage)}
						// total={4}
						siblings={isMobile ? 0 : 3}
						value={currentPage}
						onChange={setCurrentPage}
						style={{ flexWrap: "wrap", justifyContent: "center" }}
					/>
				</Flex>
				<Stack gap={24}>
					<Title order={3}>{t("myCollection.selectedThreads")}</Title>
					<Grid>
						{collectionList.map((thread) => (
							<Grid.Col span={isMobile ? 6 : 3} key={thread.id}>
								<div style={{ position: "relative" }}>
									<Button
										style={{
											position: "absolute",
											top: 0,
											right: 0,
											zIndex: 1,
										}}
										variant="subtle"
										onClick={() => handleRemoveThread(thread.id)}
									>
										<IconX size={16} />
									</Button>
									<CardPost
										thread={thread}
										date={thread.timestamp}
										isMobile={isMobile}
										isMetric={isMobile ? false : true}
									/>
								</div>
							</Grid.Col>
						))}
					</Grid>
				</Stack>
				<Button
					size={"xl"}
					radius={"xl"}
					rightSection={
						isNew ? <IconPlus size={24} /> : <IconPencilCheck size={24} />
					}
					onClick={() => {
						if (title.length < 6) {
							alert(t("myCollection.titleTooShort"));
							return;
						}
						if (description.length < 16) {
							alert(t("myCollection.descriptionTooShort"));
							return;
						}
						if (selectedThreads.length === 0) {
							alert(t("myCollection.noThreadsSelected"));
							return;
						}

						const collectionData = {
							title,
							description: description,
							threadList: collectionList,
							userID: user.profile.id,
							name: user.profile.name,
							username: user.profile.username,
							profileImage: user.profile.threads_profile_picture_url,
							createdAt: new Date().toISOString(),
						};
						console.log(selectedThreads);
						console.log(collectionData);
						if (isNew) {
							onUpdateCollection(collectionData);
							alert(t("myCollection.successCreate"));
						} else {
							onUpdateCollection(collectionData);
							alert(t("myCollection.successUpdate"));
						}
						setModalOpen(false);
					}}
				>
					{isNew ? t("myCollection.create") : t("myCollection.update")}
				</Button>
			</Stack>
		</Modal>
	);
};

export default UpdateCollectionModal;
